import moment from 'moment';
import { Address } from 'shared/types/account';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { COOKIE_NAME_ZIP_CODE, DATE_FORMAT_YYYY_MM_DD, ZIP_CODE_COOKIE_MAX_AGE } from '@/constants/constant';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import { GetEstimatedDeliveryDatePayload } from '@/sdk/composable-commerce/types/payloads/GetEstimatedDeliveryDatePayload';
import { addCookie, CookieAttributes, getCookieZipCode } from './cookieUtils';
import { maskPostalCode } from './formatUtils';

export const ShippingDisplayNameToResponseKeyValue: Record<string, string> = {
  Standard: 'smartPostDeliveryDate',
  Priority: 'groundHomeDeliveryDate',
  Express: 'fedex2DayDeliveryDate',
};

export const createFedexRequestPayload = (zipCodeValue: string, pageName?: string): GetEstimatedDeliveryDatePayload => {
  const payload: GetEstimatedDeliveryDatePayload = {
    zipCode: getFiveDigiZipCode(zipCodeValue),
    countryCode: 'US',
    pageName: pageName,
  };
  return payload;
};

export const createFedexRequestForCheckout = (
  address: Address,
  projectSettings: ProjectSettings,
  pageName?: string,
): GetEstimatedDeliveryDatePayload => {
  const postalCode = address?.postalCode ?? getZipCodeFromHeader(projectSettings);
  const payload: GetEstimatedDeliveryDatePayload = {
    zipCode: getFiveDigiZipCode(postalCode),
    countryCode: address?.country ?? 'US',
    state: address?.state,
    city: address?.city,
    pageName: pageName,
  };
  return payload;
};

export const getZipCodeFromHeader = (projectSettings: ProjectSettings): string => {
  if (!getCookieZipCode()) {
    addZipToCookie(projectSettings?.defaultZipCode ?? '');
  }
  return getCookieZipCode();
};

export const addZipToCookie = (zipCode: string) => {
  const cookieAttributes: CookieAttributes = {
    expires: ZIP_CODE_COOKIE_MAX_AGE,
  };
  addCookie(COOKIE_NAME_ZIP_CODE, zipCode, cookieAttributes);
};

export const getDeliverydateFromStorage = (zipCodeRequested: string, localStorageItemName: string) => {
  let deliveryDateFromStorage;
  const deliveryDateDataLocalStorage = localStorage.getItem(localStorageItemName);
  if (deliveryDateDataLocalStorage) {
    try {
      const deliveryDateData = JSON.parse(deliveryDateDataLocalStorage);
      if (deliveryDateData) {
        const { zipCode, deliveryDate, isSucess, date } = deliveryDateData;
        if (zipCode && zipCodeRequested && zipCode === zipCodeRequested && isSucess && date && deliveryDate) {
          const dateObj = moment.tz(date, 'America/Chicago');
          const currentDateObj = moment.tz('America/Chicago');
          const isSameDate = dateObj.isSame(currentDateObj, 'day');
          const dateObjHour = dateObj.hours();
          const currentDateHour = currentDateObj.hours();
          if (isSameDate && dateObjHour === currentDateHour) {
            deliveryDateFromStorage = deliveryDate;
          }
        }
      }
    } catch (error) {
      console.error('Error while parsing deliveryDateData');
    }
  }
  return deliveryDateFromStorage;
};

export const getArrivalDateFromResponse = (
  arrivalDate: string,
  isDropShip: boolean,
  isPersonalized: boolean,
  projectSettings: ProjectSettings,
  formatIsoString?: boolean,
  daysOfInterval?: number,
) => {
  let modifiedDate = moment.tz(parseDate(arrivalDate), 'America/Chicago');
  const intervalDays = daysOfInterval ?? 0;
  const bufferDaysForFedex = projectSettings?.bufferDaysForFedex ?? '{}';
  let bufferDays = 0;
  try {
    const selectedDivision = getSelectedDivision() ?? 'ZB';
    const bufferDaysJSON = JSON.parse(bufferDaysForFedex);
    if (bufferDaysJSON) {
      bufferDays = bufferDaysJSON?.[selectedDivision];
    }
  } catch (error) {
    console.error('Error while parsing JSON bufferDaysJSON', bufferDaysForFedex);
  }
  if (isDropShip && isPersonalized) {
    const personalizeThresholdDays = projectSettings?.fedexPersonalizeThresholdDays;
    modifiedDate = modifiedDate.add(personalizeThresholdDays, 'days');
  } else {
    if (isDropShip) {
      const dropShipThresholdDays = projectSettings?.fedexDropShipThresholdDays;
      modifiedDate = modifiedDate.add(dropShipThresholdDays, 'days');
    }
    if (isPersonalized) {
      const personalizeThresholdDays = projectSettings?.fedexPersonalizeThresholdDays;
      modifiedDate = modifiedDate.add(personalizeThresholdDays, 'days');
    }
  }
  if (daysOfInterval) {
    modifiedDate = modifiedDate.add(intervalDays, 'days');
  }
  if (bufferDays > 0) {
    modifiedDate = modifiedDate.add(bufferDays, 'days');
  }
  if (bufferDays < 0) {
    modifiedDate = modifiedDate.subtract(bufferDays * -1, 'days');
  }
  return formatIsoString
    ? modifiedDate.format(mapLocaleToMeaningfulFormat().originalEstimatedDeliveryDateFormat)
    : modifiedDate.format(mapLocaleToMeaningfulFormat().arrivalDateFormat);
};

export const getArrivalAndBackorderDateFromResponse = (
  arrivalDate: string,
  isDropShip: boolean,
  isPersonalized: boolean,
  projectSettings: ProjectSettings,
  formatIsoString?: boolean,
  daysOfInterval?: number,
) => {
  let modifiedDate = moment.tz(parseDate(arrivalDate), 'America/Chicago');
  let backorderDate = moment.tz(parseDate(arrivalDate), 'America/Chicago');
  const intervalDays = daysOfInterval ?? 0;
  if (isDropShip && isPersonalized) {
    const personalizeThresholdDays = projectSettings?.fedexPersonalizeThresholdDays;
    modifiedDate = modifiedDate.add(personalizeThresholdDays, 'days');
    backorderDate = backorderDate.add(personalizeThresholdDays, 'days');
  } else {
    if (isDropShip) {
      const dropShipThresholdDays = projectSettings?.fedexDropShipThresholdDays;
      modifiedDate = modifiedDate.add(dropShipThresholdDays, 'days');
      backorderDate = backorderDate.add(dropShipThresholdDays, 'days');
    }
    if (isPersonalized) {
      const personalizeThresholdDays = projectSettings?.fedexPersonalizeThresholdDays;
      modifiedDate = modifiedDate.add(personalizeThresholdDays, 'days');
      backorderDate = backorderDate.add(personalizeThresholdDays, 'days');
    }
  }
  if (daysOfInterval) {
    backorderDate = backorderDate.add(intervalDays, 'days');
  }
  return {
    arrivalDate: formatIsoString
      ? modifiedDate.format(mapLocaleToMeaningfulFormat().originalEstimatedDeliveryDateFormat)
      : modifiedDate.format(mapLocaleToMeaningfulFormat().arrivalDateFormat),
    backorderDate: formatIsoString
      ? backorderDate.format(mapLocaleToMeaningfulFormat().originalEstimatedDeliveryDateFormat)
      : backorderDate.format(mapLocaleToMeaningfulFormat().arrivalDateFormat),
  };
};

export const getArrivalDateForShippingMethod = (
  response: any,
  shippingMethodName: string,
  defaultDeliveryDates: any,
  projectSettings: ProjectSettings,
  isDropShip?: boolean,
  isPersonalized?: boolean,
  backorderDate?: string,
) => {
  let intervalDays = 0;
  if (backorderDate) {
    intervalDays = daysBetweenCurrentAndDate(backorderDate);
  }
  const shippingMethodResponseValue: any = ShippingDisplayNameToResponseKeyValue?.[shippingMethodName];
  const arrivalDateValue = response?.data?.fedexResponseDetails?.[shippingMethodResponseValue];
  const standardResponseValue: any = ShippingDisplayNameToResponseKeyValue?.['Standard'];
  const arrivalDateValueForStandard = response?.data?.fedexResponseDetails?.[standardResponseValue];
  let modifiedArrivalDate = moment.tz(parseDate(arrivalDateValue), 'America/Chicago');
  const bufferDaysForFedex = projectSettings?.bufferDaysForFedex ?? '{}';
  let bufferDays = 0;
  try {
    const selectedDivision = getSelectedDivision() ?? 'ZB';
    const bufferDaysJSON = JSON.parse(bufferDaysForFedex);
    if (bufferDaysJSON) {
      bufferDays = bufferDaysJSON?.[selectedDivision];
    }
  } catch (error) {
    console.error('Error while parsing JSON bufferDaysJSON', bufferDaysForFedex);
  }
  if (bufferDays > 0) {
    modifiedArrivalDate = modifiedArrivalDate.add(bufferDays, 'days');
  }
  if (bufferDays < 0) {
    modifiedArrivalDate = modifiedArrivalDate.subtract(bufferDays * -1, 'days');
  }
  let originalEstimatedDeliveryDate;
  let shippingMethodDeliveryDate;
  let shippingMethodDeliveryDateFrStandard;
  if (arrivalDateValue) {
    shippingMethodDeliveryDate = getArrivalDateFromResponse(
      arrivalDateValue,
      isDropShip ?? false,
      isPersonalized ?? false,
      projectSettings,
      false,
      intervalDays,
    );
    originalEstimatedDeliveryDate = getArrivalDateFromResponse(
      arrivalDateValue,
      isDropShip ?? false,
      isPersonalized ?? false,
      projectSettings,
      true,
      intervalDays,
    );
  }
  if (arrivalDateValueForStandard) {
    shippingMethodDeliveryDateFrStandard = getArrivalDateFromResponse(
      arrivalDateValueForStandard,
      isDropShip ?? false,
      isPersonalized ?? false,
      projectSettings,
      false,
      intervalDays,
    );
  } else {
    let defaultDeliveryDate = moment.tz('America/Chicago');
    try {
      const defaultDeliveryDatesJson = JSON.parse(defaultDeliveryDates);
      if (defaultDeliveryDatesJson && shippingMethodName && defaultDeliveryDatesJson?.['Standard']) {
        const shippingDeliveryDate = defaultDeliveryDatesJson?.[shippingMethodName];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        defaultDeliveryDate = defaultDeliveryDate.add(shippingDeliveryDate, 'days');
      }
    } catch (error) {
      console.error('Error while parsing JSON defaultDeliveryDates', defaultDeliveryDates);
    }
    shippingMethodDeliveryDateFrStandard = getDefaultDeliveryDate(
      'Standard',
      defaultDeliveryDates,
      isDropShip ?? false,
      isPersonalized ?? false,
      projectSettings,
    );
  }

  if (shippingMethodDeliveryDate) {
    return {
      modifiedDate: shippingMethodDeliveryDate,
      actualArrivalDate: modifiedArrivalDate
        ? modifiedArrivalDate.format(mapLocaleToMeaningfulFormat().arrivalDateFormat)
        : '',
      originalEstimatedDeliveryDate,
      modifiedStandardDate: shippingMethodDeliveryDateFrStandard,
    };
  } else {
    let defaultDeliveryDate = moment.tz('America/Chicago');
    try {
      const defaultDeliveryDatesJson = JSON.parse(defaultDeliveryDates);
      if (defaultDeliveryDatesJson && shippingMethodName && defaultDeliveryDatesJson[shippingMethodName]) {
        const shippingDeliveryDate = defaultDeliveryDatesJson[shippingMethodName];
        defaultDeliveryDate = defaultDeliveryDate.add(shippingDeliveryDate, 'days');
      }
    } catch (error) {
      console.error('Error while parsing JSON defaultDeliveryDates', defaultDeliveryDates, defaultDeliveryDate);
    }
    return {
      modifiedDate: getDefaultDeliveryDate(
        shippingMethodName,
        defaultDeliveryDates,
        isDropShip ?? false,
        isPersonalized ?? false,
        projectSettings,
        false,
        intervalDays,
      ),
      actualArrivalDate: getDefaultDeliveryDate(
        shippingMethodName,
        defaultDeliveryDates,
        false,
        false,
        projectSettings,
        false,
        intervalDays,
      ),
      originalEstimatedDeliveryDate: getDefaultDeliveryDate(
        shippingMethodName,
        defaultDeliveryDates,
        isDropShip ?? false,
        isPersonalized ?? false,
        projectSettings,
        true,
        intervalDays,
      ),
      modifiedStandardDate: getDefaultDeliveryDate(
        'Standard',
        defaultDeliveryDates,
        false,
        false,
        projectSettings,
        false,
        intervalDays,
      ),
    };
  }
};

export const getShippingMethodDisplayName = (shippingMethods: any, shippingMethodId: any) => {
  let displayName = '';
  if (shippingMethods && shippingMethods?.length > 0) {
    displayName = shippingMethods?.find((method: any) => method.shippingMethodId === shippingMethodId)?.displayName;
  }
  return displayName;
};

export const getDefaultDeliveryDate = (
  shippingMethodName: string,
  defaultDeliveryDates: any,
  isDropShip: boolean,
  isPersonalized: boolean,
  projectSettings: ProjectSettings,
  formatIsoString?: boolean,
  daysOfInterval?: number,
) => {
  let defaultDeliveryDate = moment.tz('America/Chicago');
  const intervalDays = daysOfInterval ?? 0;
  const shipThresholdHour: number = projectSettings?.thresholdHour;
  const weekDaysBefore11AMDays: number = projectSettings?.weekDaysBeforeThresh;
  const weekDaysAfter11AMDays: number = projectSettings?.weekDaysAfterThresh;
  const fridayDays: number = projectSettings?.fridayThresh;
  const weekedDays: number = projectSettings?.weekendThresh;
  const bufferDaysForFedex = projectSettings?.bufferDaysForFedex ?? '{}';
  let bufferDays = 0;
  let noOfDaysToBeAdded = 0;
  const currentDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const day = currentDate.getDay();
  const hour = currentDate.getHours();
  const minute = currentDate.getMinutes();
  const isWeekend = day === 0 || day === 6;
  if (day >= 1 && day <= 4) {
    const isAfterCutoff = hour > shipThresholdHour || (hour === shipThresholdHour && minute > 0);
    if (isAfterCutoff) {
      noOfDaysToBeAdded += weekDaysAfter11AMDays;
    } else {
      noOfDaysToBeAdded += weekDaysBefore11AMDays;
    }
  } else if (day === 5) {
    noOfDaysToBeAdded += fridayDays;
  } else if (isWeekend) {
    noOfDaysToBeAdded += weekedDays;
  }
  if (noOfDaysToBeAdded > 0) {
    defaultDeliveryDate = defaultDeliveryDate.add(noOfDaysToBeAdded, 'days');
  }
  try {
    const masonHolidaydatesJson = JSON.parse(projectSettings?.masonHolidaydatesJson ?? '{}');
    const defaultDeliveryDatesJson = JSON.parse(defaultDeliveryDates);
    const bufferDaysJSON = JSON.parse(bufferDaysForFedex);
    const selectedDivision = getSelectedDivision() ?? 'ZB';
    if (bufferDaysJSON) {
      bufferDays = bufferDaysJSON?.[selectedDivision];
    }
    if (defaultDeliveryDatesJson && shippingMethodName && defaultDeliveryDatesJson[shippingMethodName]) {
      const shippingDeliveryDate = defaultDeliveryDatesJson[shippingMethodName];
      defaultDeliveryDate = defaultDeliveryDate.add(shippingDeliveryDate, 'days');
    }
    if (masonHolidaydatesJson) {
      const currentDateInHolidayKeyFormat = defaultDeliveryDate.format(
        mapLocaleToMeaningfulFormat().DATE_FORMAT_DATE_MON_DATE,
      );
      if (Object.keys(masonHolidaydatesJson).includes(currentDateInHolidayKeyFormat)) {
        defaultDeliveryDate = defaultDeliveryDate.add(1, 'days');
      }
    }
  } catch (error) {
    console.error('Error while parsing JSON defaultDeliveryDates', defaultDeliveryDates);
  }
  if (isPersonalized) {
    const personalizeThresholdDays = projectSettings?.fedexPersonalizeThresholdDays;
    defaultDeliveryDate = defaultDeliveryDate.add(personalizeThresholdDays, 'days');
  } else if (isDropShip) {
    const dropShipThresholdDays = projectSettings?.fedexDropShipThresholdDays;
    defaultDeliveryDate = defaultDeliveryDate.add(dropShipThresholdDays, 'days');
  }
  if (daysOfInterval) {
    defaultDeliveryDate = defaultDeliveryDate.add(intervalDays, 'days');
  }
  if (bufferDays > 0) {
    defaultDeliveryDate = defaultDeliveryDate.add(bufferDays, 'days');
  }
  if (bufferDays < 0) {
    defaultDeliveryDate = defaultDeliveryDate.subtract(bufferDays * -1, 'days');
  }
  return formatIsoString
    ? defaultDeliveryDate.format(mapLocaleToMeaningfulFormat().originalEstimatedDeliveryDateFormat)
    : defaultDeliveryDate.format(mapLocaleToMeaningfulFormat().arrivalDateFormat);
};

export const getFiveDigiZipCode = (zipCode: string) => {
  if (zipCode?.length > 5) {
    return zipCode.substring(0, 5);
  }
  return zipCode;
};

export const setZipCodeForLoggedInCustomer = async (getAccountDataDashBoard: any, projectSettings: ProjectSettings) => {
  const response: any = await getAccountDataDashBoard();
  let address;
  if (response?.data?.address) {
    address = response?.data?.address;
  }
  const postalCode = address?.postalCode ? address?.postalCode : getZipCodeFromHeader(projectSettings);
  addZipToCookie(maskPostalCode(postalCode));
};

export const parseDate = (arrivalDate: string) => {
  const formats = [mapLocaleToMeaningfulFormat().arrivalDateFormat, mapLocaleToMeaningfulFormat().fedexResponseFormat];
  for (const format of formats) {
    const date = moment.tz(arrivalDate, format, true, 'America/Chicago');
    if (date.isValid()) {
      return date;
    }
  }
};

export const isFutureDate = (dateStr: string) => {
  return dateStr ? moment(dateStr, DATE_FORMAT_YYYY_MM_DD).isAfter(moment(), 'day') : false;
};

export const isBetweenInputAndInitialDate = (inputDate: string, initialDate: string) => {
  const today = moment().startOf('day');
  if (!inputDate || !initialDate) {
    return false;
  } else {
    return (
      moment(inputDate, DATE_FORMAT_YYYY_MM_DD).isAfter(today) &&
      moment(inputDate, DATE_FORMAT_YYYY_MM_DD).isBefore(moment(initialDate, DATE_FORMAT_YYYY_MM_DD))
    );
  }
};

export const daysBetweenCurrentAndDate = (dateStr: string) => {
  return dateStr ? moment(dateStr, DATE_FORMAT_YYYY_MM_DD).diff(moment().startOf('day'), 'days') : 0;
};
